/*
 * teamColors — derive broadcast surface variants from a team's primaryColor.
 *
 * The broadcast layout needs four shades per team:
 *   solid     The team's hex (used on the banner background gradient start,
 *             the avatar border, the status numeral, the hole-tile color).
 *   soft      ~13% alpha tint of the solid (player-row background, hole-tile
 *             background, gradient end on the side opposite the gradient).
 *   border    ~55% alpha of the solid (player-row border, hole-tile border).
 *   deep      The solid darkened ~80% toward black (banner gradient end —
 *             gives the bars their broadcast "depth" without being a flat
 *             color block).
 *
 * Travis explicitly chose "use each team's picked color" so this helper is
 * the single place that maps primaryColor → the four broadcast variants.
 * No hardcoded red/blue.
 *
 * If a team's primaryColor is null (rare — captain skipped the picker), we
 * fall back to the design's defaults (red for side 1, blue for side 2) so
 * the banner still has the broadcast feel out of the box.
 */

export interface TeamPalette {
  solid: string;
  soft: string;
  border: string;
  deep: string;
}

const FALLBACK_SIDE_1 = '#f23b49';
const FALLBACK_SIDE_2 = '#3b82f6';

/** Resolve a hex string (`#abc`, `#aabbcc`) into {r,g,b}. Returns null on
 *  parse failure; callers should fall back to the default palette. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const s = hex.replace('#', '').trim();
  if (s.length === 3) {
    const r = parseInt(s[0] + s[0], 16);
    const g = parseInt(s[1] + s[1], 16);
    const b = parseInt(s[2] + s[2], 16);
    return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? { r, g, b } : null;
  }
  if (s.length === 6) {
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? { r, g, b } : null;
  }
  return null;
}

/** Get the broadcast palette for a single team. `sideHint` is 1 or 2 —
 *  used only to pick a fallback when the team has no primaryColor. */
export function paletteFor(primaryColor: string | null | undefined, sideHint: 1 | 2 = 1): TeamPalette {
  const hex = primaryColor && /^#?[0-9a-fA-F]{3,6}$/.test(primaryColor.trim())
    ? primaryColor
    : sideHint === 2 ? FALLBACK_SIDE_2 : FALLBACK_SIDE_1;
  const rgb = hexToRgb(hex) ?? hexToRgb(FALLBACK_SIDE_1)!;
  // Darken toward black by mixing 80% of the way to (0,0,0). Closer to
  // black than CSS color-mix() because we want a near-black banner foot
  // that reads as broadcast depth, not just a faded color.
  const deepR = Math.round(rgb.r * 0.18);
  const deepG = Math.round(rgb.g * 0.18);
  const deepB = Math.round(rgb.b * 0.18);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return {
    solid:  hex.startsWith('#') ? hex : `#${hex}`,
    soft:   `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.13)`,
    border: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.55)`,
    deep:   `#${toHex(deepR)}${toHex(deepG)}${toHex(deepB)}`,
  };
}

/** Convenience: given two teams (A on side 1, B on side 2), return both
 *  palettes in one go. Safe when either team is null. */
export function teamPalettes(
  teamA: { primaryColor: string | null } | null | undefined,
  teamB: { primaryColor: string | null } | null | undefined,
): { a: TeamPalette; b: TeamPalette } {
  return {
    a: paletteFor(teamA?.primaryColor ?? null, 1),
    b: paletteFor(teamB?.primaryColor ?? null, 2),
  };
}
